import { handleSessionEnded } from '@wehunt/core-web'
import { ApiError, type ProblemDetails } from './ApiError'

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api'

export interface ApiFetchInit extends Omit<RequestInit, 'headers'> {
  token: string
  headers?: Record<string, string>
}

/**
 * The server's error body, or an empty one when there is nothing readable.
 *
 * Reading it is what lets a caller tell one 401 apart from another: the API
 * answers `{ error, code }`, and the single-logout reaction keys on `code`. A
 * body that is not json (an nginx error page, an empty 502) must not turn into a
 * parse failure that hides the status the caller actually needs.
 */
async function readProblem(res: Response): Promise<ProblemDetails> {
  try {
    const body = (await res.json()) as unknown
    return typeof body === 'object' && body !== null ? (body as ProblemDetails) : {}
  } catch {
    return {}
  }
}

export async function apiFetch<T>(path: string, init: ApiFetchInit): Promise<T> {
  const { token, headers, ...rest } = init
  const res = await fetch(`${BASE_URL}${path}`, {
    ...rest,
    headers: {
      ...headers,
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })
  if (!res.ok) {
    const error = new ApiError(res.status, await readProblem(res))
    // Wired here rather than in useApiFetch so that EVERY caller is covered: about
    // thirty call sites reach apiFetch directly (the teams api modules, the replay
    // hook, and so on), and a reaction living only in the hook left all of them
    // holding a credential whose sso session had ended.
    handleSessionEnded(error)
    throw error
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}
